import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";

import Button from "~/components/mantine/button/Button";
import { AlertCard } from "~/components/ui/card/AlertCard";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useSiteContext } from "~/contexts/SiteContext";
import { useTeam } from "~/contexts/TeamProvider";
import { parsePaginationHeaders } from "~/helpers/paginationHeaders";
import { expandSourceFilter } from "~/helpers/sourceFilter";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import { apiClient } from "../../../api/ApiClient";
import { queryKeys } from "../../../api/queryKeys";
import Content from "../../../components/automations/Content";
import { ContentItem } from "../../../components/automations/ContentItems";
import SourceFilter from "../../../components/automations/components/SourceFilter";
import Switch from "../../../components/switch/Switch";
import { PaginationConfig } from "../../../components/ui/pagination/Pagination";
import { AutomationAuthorizationProvider } from "../../../contexts/AutomationAuthorizationContext";
import styles from "./CustomizationsPage.module.scss";

interface CustomizationGroupConfig {
  sectionTitle: string;
  group: string;
  pageTitle: string;
  description?: string;
}

// Backend stores no descriptions for customization groups, so the copy
// lives here (recommendation categories get theirs from the API instead).
const customizationGroups: Record<
  "ai" | "schema" | "ai_instructions" | "enhancement",
  CustomizationGroupConfig
> = {
  ai: {
    sectionTitle: "AI Enhancements",
    group: "ai",
    pageTitle: PAGE_TITLES.content,
    description:
      "Good titles and descriptions help search and AI understand your pages and improve click-through rates.",
  },
  schema: {
    sectionTitle: "Pages Markup",
    group: "schema",
    pageTitle: PAGE_TITLES.schema,
    description:
      "Schema markup describes your pages to search engines and AI in a machine-readable format. We generate the markup for each page automatically.",
  },
  ai_instructions: {
    sectionTitle: "AI Instructions",
    group: "ai_instructions",
    pageTitle: PAGE_TITLES.aiInstructions,
    description:
      "AI Instructions give AI bots a concise, structured summary with suggested matching prompts for each page, helping AI understand and reference your content to searchers.",
  },
  enhancement: {
    sectionTitle: "Customizations",
    group: "enhancement",
    pageTitle: PAGE_TITLES.liveEditor,
    description:
      "These are code and content customizations to your site made via the Live Editor Chrome Extension.",
  },
};

type CustomizationPageProps = { group: keyof typeof customizationGroups };
const CustomizationsPage = ({ group }: CustomizationPageProps) => {
  const { id } = useParams();

  if (!id) {
    throw new Error("CustomizationsPage: missing site id in params");
  }

  const { team } = useTeam();
  const siteContext = useSiteContext();

  const location = useLocation();

  // Set the page title based on the customization group
  usePageTitle(customizationGroups[group].pageTitle);

  const [allData, setAllData] = useState<ContentItem[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<ContentItem | null>(null);

  const loadedPagesRef = useRef<number[]>([]);

  const isFirstLoad = useRef(true);
  const prevPathRef = useRef(location.pathname);

  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState<
    "auto" | "manual" | undefined
  >(undefined);
  const [outdatedFilter, setOutdatedFilter] = useState<boolean | undefined>(
    undefined,
  );

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 18,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 10,
  });

  const isRefetchingRef = useRef(false);
  const { data: widgetCustomizationsStats, isFetching: isFetchingStats } =
    useQuery({
      queryKey: queryKeys.widgetCustomizationsStats(
        group,
        sourceFilter,
        outdatedFilter,
      ),
      queryFn: async () => {
        // BE accepts an array of `source` values; generated type narrows to
        // a single value, hence the cast. See expandSourceFilter.
        const apiSources = expandSourceFilter(sourceFilter);
        const statsQuery =
          apiSources || outdatedFilter
            ? ({
                ...(apiSources ? { source: apiSources } : {}),
                ...(outdatedFilter ? { outdated: true } : {}),
              } as Parameters<
                typeof apiClient.sites.widgetCustomizationsStatsDetail
              >[2])
            : undefined;
        // ai.md has a dedicated stats endpoint (no `variant` segment); the
        // other variants share the generic one.
        const response =
          group === "ai_instructions"
            ? await apiClient.sites.widgetCustomizationsAiInstructionsStatsDetail(
                Number(id),
                statsQuery as Parameters<
                  typeof apiClient.sites.widgetCustomizationsAiInstructionsStatsDetail
                >[1],
              )
            : await apiClient.sites.widgetCustomizationsStatsDetail(
                Number(id),
                group,
                statsQuery,
              );

        return response?.data?.["widget/customization_stats"];
      },
      enabled: !!id,
    });

  const refetchAllPages = async (showLoadingState = false) => {
    if (loadedPagesRef.current.length === 0 || isRefetchingRef.current) return;
    isRefetchingRef.current = true;

    try {
      if (showLoadingState) {
        setIsInitialLoading(true);
      }

      const allPagesData: ContentItem[] = [];

      // UI "Manual" captures both manual and regenerate — pass both as an array.
      const apiSources = expandSourceFilter(sourceFilter);

      for (const pageNum of loadedPagesRef.current) {
        const response = await apiClient.sites.pagesCustomizedDetail(
          Number(id),
          {
            variant: group,
            source: apiSources,
            "page[size]": pagination.itemsPerPage,
            "page[number]": pageNum,
            s: "path_and_query ASC",
            q: {
              // @ts-expect-error BE types - missing input field
              path_and_query_cont: searchTerm,
              ...(outdatedFilter && group === "ai"
                ? { ai_customized_outdated: true as const }
                : {}),
              ...(outdatedFilter && group === "schema"
                ? { schema_customized_outdated: true as const }
                : {}),
              ...(outdatedFilter && group === "ai_instructions"
                ? { ai_instructions_customized_outdated: true as const }
                : {}),
            },
          },
        );
        const pageData = response?.data?.page_customizations || [];
        allPagesData.push(...pageData);
      }

      setAllData(allPagesData);

      // TODO: investigate if currentPage update is needed after refetch
      // if (currentPage) {
      //   const updatedCurrentPage = allPagesData.find(
      //     (item: ContentItem) => item.id === currentPage.id,
      //   );
      //   if (updatedCurrentPage) {
      //     setCurrentPage(updatedCurrentPage);
      //   }
      // }
    } catch (error) {
      console.error("Error refetching pages:", error);
    } finally {
      isRefetchingRef.current = false;
      setIsInitialLoading(false);
    }
  };
  const { isFetching, isLoading } = useQuery({
    queryKey: queryKeys.customizedPages(
      group,
      pagination.currentPage,
      searchTerm,
      sourceFilter,
      outdatedFilter,
    ),
    queryFn: async () => {
      // UI "Manual" captures both manual and regenerate — pass both as an array.
      const apiSources = expandSourceFilter(sourceFilter);
      const response = await apiClient.sites.pagesCustomizedDetail(Number(id), {
        variant: group,
        source: apiSources,
        "page[size]": pagination.itemsPerPage,
        "page[number]": pagination.currentPage,
        s: "path_and_query ASC",
        q: {
          // @ts-expect-error BE types - missing input field
          path_and_query_cont: searchTerm,
          ...(outdatedFilter && group === "ai"
            ? { ai_customized_outdated: true as const }
            : {}),
          ...(outdatedFilter && group === "schema"
            ? { schema_customized_outdated: true as const }
            : {}),
          ...(outdatedFilter && group === "ai_instructions"
            ? { ai_instructions_customized_outdated: true as const }
            : {}),
        },
      });

      const { headers } = response;
      const shouldKeepLoading =
        isFirstLoad.current && pagination.currentPage === 1;
      setIsInitialLoading(shouldKeepLoading && isLoading);

      if (isFirstLoad.current) {
        isFirstLoad.current = false;
      }

      const paginationHeaders = parsePaginationHeaders(headers);
      setPagination((prev: PaginationConfig) => ({
        ...prev,
        totalItems: paginationHeaders.totalCount,
        nextPage: paginationHeaders.nextPage,
        prevPage: paginationHeaders.prevPage,
        totalPages: paginationHeaders.totalPages,
      }));
      const resp = response?.data?.page_customizations || [];

      if (!loadedPagesRef.current.includes(pagination.currentPage)) {
        loadedPagesRef.current.push(pagination.currentPage);
      }

      setAllData((prev) => {
        if (pagination.currentPage === 1) {
          return resp || [];
        }

        const newItems = resp || [];
        const existingIds = new Set(prev.map((item) => item.id));
        const uniqueNewItems = newItems.filter(
          (item: ContentItem) => !existingIds.has(item.id),
        );

        return [...prev, ...uniqueNewItems];
      });

      return resp;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      setIsInitialLoading(true);
      loadedPagesRef.current = [];
      setAllData([]);
      setCurrentPage(null);
      setInputValue("");
      setSearchTerm("");
      setSourceFilter(undefined);
      setOutdatedFilter(undefined);

      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
      }));
      isFirstLoad.current = true;
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  const handleSearchChange = (value: string) => {
    setInputValue(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, 500);
    if (value.length === 0) {
      setCurrentPage(null);
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setAllData([]);
    setCurrentPage(null);
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    loadedPagesRef.current = [];
  }, [searchTerm, sourceFilter, outdatedFilter]);

  // Keep currentPage in sync with allData — select first available if current
  // page was removed from the filtered list (e.g. after delete or source change)
  useEffect(() => {
    if (allData.length === 0) {
      setCurrentPage(null);

      return;
    }

    if (!currentPage) {
      setCurrentPage(allData[0]);

      return;
    }

    const updated = allData.find((item) => item.id === currentPage.id);
    if (!updated) {
      setCurrentPage(allData[0]);
    } else if (
      updated.approved !== currentPage.approved ||
      updated.total !== currentPage.total
    ) {
      // Sync stale approved/total counts after approve or refetch
      setCurrentPage(updated);
    }
  }, [allData, currentPage]);

  useEffect(() => {
    if (!isLoading && !isFetching && isInitialLoading) {
      setIsInitialLoading(false);
    }
  }, [isLoading, isFetching, isInitialLoading]);

  // ai.md instructions are generated for every site (preview for non-engine
  // teams), so this group stays reachable without the AI Visibility Engine;
  // editing/approving is enforced read-only by the backend. The other groups
  // still require the engine.
  if (team && !team.ai_engine_available && group !== "ai_instructions") {
    return <Navigate to={routes.site.root({ id })} replace />;
  }

  // ai.md is generated for every site, but it's only served to LLMs when this
  // site's AI Visibility Engine (prerendering) is on. Drive the "enable it" CTA
  // off that flag; the actions themselves are gated by their own abilities.
  const site = siteContext?.site?.site ?? null;
  const showEngineCta =
    group === "ai_instructions" && site != null && !site.prerendering;

  return (
    <AutomationAuthorizationProvider>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            {customizationGroups[group].pageTitle}
          </h1>
          <div className={styles.filters}>
            {group !== "enhancement" && (
              <label className={styles.outdatedToggle}>
                Show updates
                <Switch
                  checked={!!outdatedFilter}
                  onChange={(e) =>
                    setOutdatedFilter(
                      e.currentTarget.checked ? true : undefined,
                    )
                  }
                />
              </label>
            )}
            <SourceFilter
              sourceFilter={sourceFilter}
              onSourceFilterChange={setSourceFilter}
            />
          </div>
        </div>
        {showEngineCta && (
          <AlertCard
            type="info"
            withIcon
            content="AI Instructions help AI assistants like ChatGPT, Claude and Gemini understand your pages. You can preview them here, but editing and approving stay locked until you turn on the AI Visibility Engine for this site."
            actions={
              <Link to={routes.site.visibilityEngineSettings({ id })}>
                <Button>Enable AI Visibility Engine</Button>
              </Link>
            }
          />
        )}
        <Content
          isFetchingStats={isFetchingStats}
          pagesStats={widgetCustomizationsStats}
          group={group}
          title={customizationGroups[group].sectionTitle}
          description={customizationGroups[group].description}
          data={allData}
          pagination={pagination}
          setPagination={setPagination}
          isPagesLoading={isFetching}
          isInitialLoading={isInitialLoading}
          setSearchTerm={handleSearchChange}
          searchTerm={inputValue}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          onMutationSuccess={refetchAllPages}
          sourceFilter={sourceFilter}
          outdatedFilter={outdatedFilter}
        />
      </div>
    </AutomationAuthorizationProvider>
  );
};

export default CustomizationsPage;
